import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { getPriorityLegend } from './emma-table-row-cell-priority.i18n';
import { EMMATableRowCellPriorityService } from './emma-table-row-cell-priority.service';
import { Subscription } from 'rxjs';
import { debounce } from 'lodash';
@Component({
    selector: 'emma-table-row-cell-priority',
    templateUrl: './emma-table-row-cell-priority.component.html',
    styleUrls: ['./emma-table-row-cell-priority.component.css'],
    standalone: false
})
export class EMMATableRowCellPriorityComponent implements AfterViewChecked, OnInit, OnDestroy {
  @Output() $priorityChange = new EventEmitter<number>();
  @Input() value: number | string = '';
  @Input() title = '';
  @Input() isEditable = false;

  isSliderVisible = false;
  isSliderTop = false;
  isNeededAdjustement = false;

  readonly MAX_PRIORITY = 9;
  readonly MIN_PRIORITY = 0;
  readonly TICK_STEP = 1;
  readonly DELAY = 750;

  priorityLegend = getPriorityLegend;

  private cellClickedSubscription!: Subscription;
  private isCurrentCellClicked = false;
  private mouseLeaveTimeout: NodeJS.Timeout | undefined;
  private scrollListener: (() => void) | null = null;

  onPriorityChange = debounce((value: number) => {
    this.$priorityChange.emit(value);
  }, this.DELAY);

  constructor(
    private readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly tableRowCellPriorityService: EMMATableRowCellPriorityService
  ) {}

  ngOnInit() {
    this.cellClickedSubscription = this.tableRowCellPriorityService.cellClicked$.subscribe(() => {
      if (!this.isCurrentCellClicked) {
        if (this.isSliderVisible) {
          this.isSliderVisible = false;
          this.removeScrollListener();
        }
      } else {
        this.isCurrentCellClicked = false;
      }
    });
  }

  ngOnDestroy() {
    if (this.cellClickedSubscription) {
      this.cellClickedSubscription.unsubscribe();
    }
    if (this.mouseLeaveTimeout) {
      clearTimeout(this.mouseLeaveTimeout);
    }
    this.removeScrollListener();
  }

  getDataTypePriorityColor(value: number): string {
    const colorMap: { [key: number]: string } = {
      1: '#D1D2D6',
      2: '#B7B8BA',
      3: '#B7B8BA',
      4: '#00000080',
      5: '#00000080',
      6: '#04A163',
      7: '#01A263',
      8: '#01A263',
    };

    return colorMap[value] || '#00E08B';
  }

  onClickCell() {
    this.closeTimeout();
    this.isCurrentCellClicked = true;
    this.isSliderVisible = !this.isSliderVisible;
    this.tableRowCellPriorityService.notifyOnCellClicked();
    if (this.isSliderVisible) {
      this.isNeededAdjustement = true;
      this.addScrollListener();
    } else {
      this.removeScrollListener();
    }
  }

  ngAfterViewChecked() {
    if (this.isNeededAdjustement) {
      this.adjustSliderPosition();
      this.isNeededAdjustement = false;
    }
  }

  adjustSliderPosition() {
    const slider = this.el.nativeElement.querySelector('.slider');
    const rect = slider.getBoundingClientRect();
    const parentRect = this.el.nativeElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const offset = 50;

    if (rect.right > viewportWidth) {
      this.renderer.setStyle(slider, 'left', 'auto');
      this.renderer.setStyle(slider, 'right', `${offset}px`);
      this.renderer.setStyle(slider, 'top', `${parentRect.bottom}px`);
      this.renderer.setStyle(slider, 'bottom', 'auto');
    } else {
      this.renderer.setStyle(slider, 'left', `${parentRect.left}px`);
      this.renderer.setStyle(slider, 'right', 'auto');
      this.renderer.setStyle(slider, 'top', `${parentRect.bottom}px`);
      this.renderer.setStyle(slider, 'bottom', 'auto');
    }
  }

  addScrollListener() {
    if (!this.scrollListener) {
      this.scrollListener = this.renderer.listen('window', 'scroll', () => {
        if (this.isSliderVisible) {
          this.isSliderVisible = false;
          this.removeScrollListener();
        }
      });
    }
  }

  closeTimeout(): void {
    if (this.mouseLeaveTimeout) {
      clearTimeout(this.mouseLeaveTimeout);
      this.mouseLeaveTimeout = undefined;
    }
  }

  onMouseEnter(): void {
    this.closeTimeout();
  }

  onMouseLeave() {
    this.mouseLeaveTimeout = setTimeout(() => {
      this.isSliderVisible = false;
      this.removeScrollListener();
    }, this.DELAY);
  }

  onBlur() {
    this.onMouseLeave();
  }

  onFocus() {
    this.onMouseEnter();
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.onClickCell();
    }
  }
  removeScrollListener() {
    if (this.scrollListener) {
      this.scrollListener();
      this.scrollListener = null;
    }
  }
}
